import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  catchError,
} from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';

import { PermissionService } from 'app/core/permission/services/permission.service';
import { ROUTES_PERMISSIONS } from 'app/common/constants/permission';
import { PermissionDirective } from 'app/core/permission/directives/permission.directive';

import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { AssignTerritoryModalComponent } from 'app/modules/components/routing/sub-routes/client-route-locator/assign-territory-modal/assign-territory-modal.component';
import { AssignTerritoryModalResult } from 'app/modules/components/routing/sub-routes/client-route-locator/assign-territory-modal/interfaces/assign-territory-modal.interface';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { ClientFrequencyDisplayComponent } from 'app/modules/components/routing/sub-routes/client-route-locator/client-frequency-display/client-frequency-display.component';
import { FrequencyUpdateEvent } from 'app/modules/components/routing/sub-routes/client-route-locator/client-frequency-display/client-frequency-display.interface';
import {
  BusinessPartnerWithRoutes,
  RouteInfo,
  SubRouteInfo,
} from 'app/modules/components/routing/sub-routes/client-route-locator/interfaces/client-route-locator.interface';
import { AddressMapButtonComponent } from 'app/shared/components/address-map-button/address-map-button.component';
import { BusinessPartnerNavigationComponent } from 'app/shared/components/business-partner-navigation/business-partner-navigation.component';
import { RouteNavigationComponent } from 'app/shared/components/route-navigation/route-navigation.component';
import { SubRouteNavigationComponent } from 'app/shared/components/sub-route-navigation/sub-route-navigation.component';

@Component({
  selector: 'app-business-partners-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule,
    MatMenuModule,
    ClientFrequencyDisplayComponent,
    PermissionDirective,
    AddressMapButtonComponent,
    BusinessPartnerNavigationComponent,
    RouteNavigationComponent,
    SubRouteNavigationComponent
  ],
  templateUrl: './business-partners-tab.component.html',
  styleUrl: './business-partners-tab.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class BusinessPartnersTabComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly businessPartnerService: BusinessPartnerService;
  private readonly handlerMessage: HandlerMessageService;
  private readonly dialog: MatDialog;
  private readonly permissionService: PermissionService;
  private menuCloseTimeout: any;
  private readonly MAX_VISIBLE_TERRITORIES = 4;

  readonly PERMISSIONS = ROUTES_PERMISSIONS.ASSOCIATIONS;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Form controls
  searchControl = new FormControl('');

  // Data properties
  dataSource = new MatTableDataSource<BusinessPartnerWithRoutes>([]);

  // Pagination properties
  totalItems = 0;
  pageSize = 10;
  currentPage = 0;
  pageSizeOptions = [5, 10, 25, 50];

  // UI states
  isLoading = false;

  // Table configuration
  displayedColumns: string[] = ['expand', 'mapa', 'info', 'location', 'territorios', 'acciones'];
  expandedElement: BusinessPartnerWithRoutes | null = null;

  // State preservation for expanded row
  private expandedElementId: string | null = null;

  constructor(
    businessPartnerService: BusinessPartnerService,
    handlerMessage: HandlerMessageService,
    dialog: MatDialog,
    permissionService: PermissionService
  ) {
    this.businessPartnerService = businessPartnerService;
    this.handlerMessage = handlerMessage;
    this.dialog = dialog;
    this.permissionService = permissionService;
  }

  ngOnInit(): void {
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.menuCloseTimeout) {
      clearTimeout(this.menuCloseTimeout);
    }
  }

  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(searchTerm => {
          this.currentPage = 0;
          this.clearExpandedState();
          return this.loadClients(searchTerm);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private loadClients(searchTerm?: string): Observable<any> {
    this.isLoading = true;

    const observer = this.businessPartnerService.searchBusinessPartnersWithRoutes(
      searchTerm,
      this.currentPage,
      this.pageSize
    ).pipe(
      catchError(() => {
        this.showErrorMessage('Error al cargar clientes');
        return of({
          status: 'error',
          statusCode: '500',
          data: {
            data: [],
            pageInfo: [{ total: 0 }]
          }
        });
      }),
      takeUntil(this.destroy$)
    );

    observer.subscribe(response => {
      this.isLoading = false;

      if (response?.data?.data) {
        this.dataSource.data = response.data.data;
        this.restoreExpandedState();

        if (response.data.pageInfo?.length > 0) {
          this.totalItems = response.data.pageInfo[0].total;
        } else {
          this.totalItems = response.data.data.length;
        }
      } else {
        this.dataSource.data = [];
        this.totalItems = 0;
        this.expandedElement = null;
        this.expandedElementId = null;
      }
    });

    return observer;
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.clearExpandedState();
    this.loadClients(this.searchControl.value || '');
  }

  getClientDisplayName(client: BusinessPartnerWithRoutes): string {
    const code = client.externalKeys?.code || client.id?.toString() || '';
    const name = client.commercialName || client.name || '';
    return code ? `${code} - ${name}` : name;
  }

  getClientAddress(client: BusinessPartnerWithRoutes): string {
    if (!client.principalAddress) return 'Sin dirección';

    const parts = [];
    if (client.principalAddress.address) parts.push(client.principalAddress.address);
    if (client.principalAddress.locationThree?.name) parts.push(client.principalAddress.locationThree.name);
    if (client.principalAddress.locationTwo?.name) parts.push(client.principalAddress.locationTwo.name);

    return parts.join(', ') || 'Sin dirección';
  }

  /** Address label (name) with the street address as fallback. */
  getAddressName(client: BusinessPartnerWithRoutes): string {
    const address = client.principalAddress;
    if (!address) return 'Sin dirección';
    return address.name || address.address || 'Sin dirección';
  }

  /** "Departamento, Ciudad" from locationTwo (department) and locationThree (city). */
  getAddressLocations(client: BusinessPartnerWithRoutes): string {
    const address = client.principalAddress;
    if (!address) return '';

    const parts = [];
    if (address.locationTwo?.name) parts.push(address.locationTwo.name);
    if (address.locationThree?.name) parts.push(address.locationThree.name);

    return parts.join(', ');
  }

  toggleExpandRow(client: BusinessPartnerWithRoutes): void {
    this.expandedElement = this.expandedElement === client ? null : client;
    this.expandedElementId = this.expandedElement ? this.expandedElement._id : null;
  }

  hasRoutes(client: BusinessPartnerWithRoutes): boolean {
    return (client.subRoutes && client.subRoutes.length > 0) || false;
  }

  getRoutesCount(client: BusinessPartnerWithRoutes): number {
    return client.subRoutes?.length || 0;
  }

  getRoutesWithFrequency(client: BusinessPartnerWithRoutes): number {
    if (!client.subRoutes || client.subRoutes.length === 0) return 0;
    return client.subRoutes.filter(subRoute =>
      subRoute.frequencies && subRoute.frequencies.length > 0
    ).length;
  }

  getRoutesWithoutFrequency(client: BusinessPartnerWithRoutes): number {
    if (!client.subRoutes || client.subRoutes.length === 0) return 0;
    return client.subRoutes.filter(subRoute =>
      !subRoute.frequencies || subRoute.frequencies.length === 0
    ).length;
  }

  hasFrequencies(subRoute: SubRouteInfo): boolean {
    return subRoute.frequencies !== undefined &&
           subRoute.frequencies !== null &&
           subRoute.frequencies.length > 0;
  }

  getTerritories(client: BusinessPartnerWithRoutes): RouteInfo[] {
    return client.routes || [];
  }

  getTerritoriesToDisplay(client: BusinessPartnerWithRoutes) {
    const territories = this.getTerritories(client);
    return {
      visible: territories.slice(0, this.MAX_VISIBLE_TERRITORIES),
      hasMore: territories.length > this.MAX_VISIBLE_TERRITORIES,
      moreCount: territories.length - this.MAX_VISIBLE_TERRITORIES,
      total: territories.length
    };
  }

  closeMenuWithDelay(trigger: MatMenuTrigger): void {
    this.menuCloseTimeout = setTimeout(() => {
      trigger.closeMenu();
    }, 100);
  }

  cancelMenuClose(): void {
    if (this.menuCloseTimeout) {
      clearTimeout(this.menuCloseTimeout);
      this.menuCloseTimeout = null;
    }
  }

  openAssignTerritoryDialog(client: BusinessPartnerWithRoutes): void {
    if (!this.checkIfHasPermission(this.PERMISSIONS.BUSINESS_PARTNER, 'No tienes permisos para asignar territorios.')) {
      return;
    }
    const dialogRef = this.dialog.open(AssignTerritoryModalComponent, {
      width: '95%',
      maxWidth: '1200px',
      height: '95%',
      maxHeight: '800px',
      data: { clientId: client._id },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: AssignTerritoryModalResult) => {
      if (result?.success) {
        this.handlerMessage.success({
          message: `${result.territoriesAssigned} territorio(s) asignado(s) correctamente`
        });
        this.refreshClientData();
      }
    });
  }

  getTerritoriesDisplay(client: BusinessPartnerWithRoutes): string {
    const territories = this.getTerritories(client);
    if (territories.length === 0) return 'Sin territorios';

    return territories.map(territory => {
      const code = territory.externalKeys?.code;
      return code ? `${code} - ${territory.name}` : territory.name;
    }).join(', ');
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.currentPage = 0;
    this.clearExpandedState();
    this.loadClients();
  }

  onFrequencyUpdated(event: FrequencyUpdateEvent): void {
    this.showSuccessMessage(
      `Frecuencia ${event.action === 'updated' ? 'actualizada' : event.action === 'created' ? 'creada' : 'eliminada'} correctamente`
    );
    this.refreshClientData();
  }

  onRefreshRequested(): void {
    this.refreshClientData();
  }

  private refreshClientData(): void {
    if (this.expandedElement) {
      this.expandedElementId = this.expandedElement._id;
    }
    this.loadClients(this.searchControl.value || '');
  }

  private restoreExpandedState(): void {
    if (this.expandedElementId && this.dataSource.data.length > 0) {
      const clientToExpand = this.dataSource.data.find(
        (client: BusinessPartnerWithRoutes) => client._id === this.expandedElementId
      );

      if (clientToExpand && this.hasRoutes(clientToExpand)) {
        this.expandedElement = clientToExpand;
      } else {
        this.expandedElement = null;
        this.expandedElementId = null;
      }
    }
  }

  private clearExpandedState(): void {
    this.expandedElement = null;
    this.expandedElementId = null;
  }

  private showErrorMessage(message: string): void {
    this.handlerMessage.error({ message });
  }

  private showSuccessMessage(message: string): void {
    this.handlerMessage.success({ message });
  }

  private checkIfHasPermission(permission: string, errorMessage?: string): boolean {
    const has = this.permissionService.has(permission);
    if (!has && errorMessage) {
      this.handlerMessage.error({
        message: errorMessage,
      });
    }
    return has;
  }
}
